// src/components/PwaInstallPrompt.js
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaDownload, FaShareSquare, FaTimes } from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';
import './PwaInstallPrompt.css';

const LEGACY_DISMISSED_SESSION_KEY = 'pwa-install-prompt-dismissed';
const DISMISSED_UNTIL_KEY = 'pwa-install-prompt-dismissed-until';
const PROMPT_DELAY_MS = 5000;
const DISMISSAL_COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000;

const isRunningStandalone = () => {
  const isStandaloneDisplay = window.matchMedia?.('(display-mode: standalone)').matches;
  const isIosStandalone = window.navigator.standalone === true;

  return Boolean(isStandaloneDisplay || isIosStandalone);
};

const isIosDevice = () => {
  const userAgent = window.navigator.userAgent || '';
  const isClassicIos = /iPad|iPhone|iPod/.test(userAgent);
  const isModernIpad = window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1;

  return isClassicIos || isModernIpad;
};

const isIosSafari = () => {
  const userAgent = window.navigator.userAgent || '';
  const usesSafari = /Safari/i.test(userAgent);
  const isAnotherIosBrowser = /CriOS|FxiOS|EdgiOS|OPiOS/i.test(userAgent);

  return usesSafari && !isAnotherIosBrowser;
};

const isPromptSuppressed = () => {
  try {
    if (window.sessionStorage.getItem(LEGACY_DISMISSED_SESSION_KEY) === 'true') {
      return true;
    }
  } catch (error) {
    // A ausência do armazenamento de sessão não impede a exibição do convite.
  }

  try {
    const dismissedUntil = Number(window.localStorage.getItem(DISMISSED_UNTIL_KEY));
    return Number.isFinite(dismissedUntil) && dismissedUntil > Date.now();
  } catch (error) {
    return false;
  }
};

const rememberDismissal = () => {
  try {
    const dismissedUntil = Date.now() + DISMISSAL_COOLDOWN_MS;
    window.localStorage.setItem(DISMISSED_UNTIL_KEY, String(dismissedUntil));
  } catch (error) {
    try {
      window.sessionStorage.setItem(LEGACY_DISMISSED_SESSION_KEY, 'true');
    } catch (sessionError) {
      // O convite ainda pode ser fechado quando o armazenamento do navegador está indisponível.
    }
  }
};

const PwaInstallPrompt = () => {
  const { corApp, corFonteApp, loadingTheme } = useTheme();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [flow, setFlow] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [showIosGuidance, setShowIosGuidance] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [installError, setInstallError] = useState('');
  const installInProgressRef = useRef(false);

  const hidePrompt = useCallback(() => {
    setDeferredPrompt(null);
    setIsVisible(false);
    setShowIosGuidance(false);
    setInstallError('');
  }, []);

  const dismissPrompt = useCallback(() => {
    rememberDismissal();
    hidePrompt();
  }, [hidePrompt]);

  useEffect(() => {
    const displayModeQuery = window.matchMedia?.('(display-mode: standalone)');
    let revealTimer;

    const schedulePrompt = (nextFlow) => {
      window.clearTimeout(revealTimer);
      setFlow(nextFlow);
      setShowIosGuidance(false);

      revealTimer = window.setTimeout(() => {
        if (!isRunningStandalone() && !isPromptSuppressed()) {
          setIsVisible(true);
        }
      }, PROMPT_DELAY_MS);
    };

    const handleInstalledApp = () => {
      window.clearTimeout(revealTimer);
      hidePrompt();
    };

    const handleDisplayModeChange = (event) => {
      if (event.matches) {
        handleInstalledApp();
      }
    };

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();

      if (isRunningStandalone() || isPromptSuppressed()) {
        return;
      }

      setDeferredPrompt(event);
      schedulePrompt('native');
    };

    if (!isRunningStandalone() && !isPromptSuppressed() && isIosDevice()) {
      schedulePrompt(isIosSafari() ? 'ios-safari' : 'ios-other');
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleInstalledApp);

    if (displayModeQuery?.addEventListener) {
      displayModeQuery.addEventListener('change', handleDisplayModeChange);
    } else if (displayModeQuery?.addListener) {
      displayModeQuery.addListener(handleDisplayModeChange);
    }

    return () => {
      window.clearTimeout(revealTimer);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleInstalledApp);

      if (displayModeQuery?.removeEventListener) {
        displayModeQuery.removeEventListener('change', handleDisplayModeChange);
      } else if (displayModeQuery?.removeListener) {
        displayModeQuery.removeListener(handleDisplayModeChange);
      }
    };
  }, [hidePrompt]);

  const handleInstall = async () => {
    if (!deferredPrompt || installInProgressRef.current) {
      return;
    }

    installInProgressRef.current = true;
    setIsInstalling(true);
    setInstallError('');

    try {
      let promptResult = await deferredPrompt.prompt();

      if (!promptResult?.outcome && deferredPrompt.userChoice) {
        promptResult = await deferredPrompt.userChoice;
      }

      if (promptResult?.outcome === 'dismissed') {
        rememberDismissal();
      }

      hidePrompt();
    } catch (error) {
      setInstallError('Não foi possível abrir a instalação. Tente novamente pelo menu do navegador.');
    } finally {
      installInProgressRef.current = false;
      setIsInstalling(false);
    }
  };

  const handleAccept = () => {
    if (flow === 'native') {
      handleInstall();
      return;
    }

    setInstallError('');
    setShowIosGuidance(true);
  };

  if (!isVisible || loadingTheme) {
    return null;
  }

  const isNativeFlow = flow === 'native';
  const isSafariFlow = flow === 'ios-safari';
  const title = showIosGuidance ? 'Instalar no iPhone' : 'Deseja instalar o app de pedidos?';

  return (
    <aside
      className="pwa-install-card"
      role="region"
      aria-labelledby="pwa-install-title"
      aria-describedby={showIosGuidance ? 'pwa-install-description' : undefined}
    >
      <button
        type="button"
        className="pwa-install-close"
        onClick={dismissPrompt}
        aria-label="Fechar convite de instalação"
      >
        <FaTimes aria-hidden="true" />
      </button>

      <div className="pwa-install-heading">
        <div className="pwa-install-icon" style={{ backgroundColor: corApp, color: corFonteApp }} aria-hidden="true">
          {isNativeFlow ? <FaDownload /> : <FaShareSquare />}
        </div>
        <h2 id="pwa-install-title">{title}</h2>
      </div>

      {showIosGuidance ? (
        <div id="pwa-install-description" className="pwa-install-instructions">
          {!isSafariFlow && (
            <p className="pwa-install-browser-note">
              Se a opção não aparecer, abra este endereço no Safari e tente novamente.
            </p>
          )}
          <p><strong>1.</strong> Toque em <strong>Compartilhar</strong>.</p>
          <p><strong>2.</strong> Escolha <strong>Adicionar à Tela de Início</strong>.</p>
          <p><strong>3.</strong> Confirme em <strong>Adicionar</strong>.</p>
        </div>
      ) : null}

      {installError && <p className="pwa-install-error" role="alert">{installError}</p>}

      <div className="pwa-install-actions">
        {showIosGuidance ? (
          <button
            type="button"
            className="pwa-install-primary"
            style={{ backgroundColor: corApp, color: corFonteApp }}
            onClick={dismissPrompt}
          >
            Entendi
          </button>
        ) : (
          <>
            <button
              type="button"
              className="pwa-install-primary"
              style={{ backgroundColor: corApp, color: corFonteApp }}
              onClick={handleAccept}
              disabled={isInstalling}
            >
              {isInstalling ? 'Abrindo...' : 'Sim'}
            </button>
            <button type="button" className="pwa-install-secondary" onClick={dismissPrompt}>
              Não
            </button>
          </>
        )}
      </div>
    </aside>
  );
};

export default PwaInstallPrompt;
